import type { Serie } from '@scality/core-ui/dist/next';
import { useMemo } from 'react';
import { useSeriesColor } from './useSeriesColor';

type LineChartSeries = {
  label: string;
  data: { x: number; y: number }[];
  isLineDashed?: boolean;
}[];

type UseLineChartParams = {
  series: LineChartSeries;
  /**
   * The queried window in unix ms, stamped by the backend on every chart of one render so they
   * share an axis. Absent for chat charts and for `all`, where the data span is used instead.
   */
  startTime?: number | null;
  endTime?: number | null;
  /** Sampling step in seconds, for gap filling. Absent below 24h, where nothing is downsampled. */
  interval?: number | null;
};

export const useLineChartParams = ({ series, startTime, endTime, interval }: UseLineChartParams) => {
  const coreUiSeries: Serie[] = useMemo(() => {
    return series.map((serie) => ({
      resource: serie.label,
      data: serie.data.map(({ x, y }) => [x / 1000, y]),
      getTooltipLabel: () => serie.label,
      isLineDashed: serie.isLineDashed,
    }));
  }, [series]);

  const colorSet = useSeriesColor(series);

  const startingTimeStamp = useMemo(() => {
    if (startTime != null) return startTime / 1000;
    return series.reduce(
      (min, serie) => Math.min(min, (serie.data[0]?.x ?? Number.POSITIVE_INFINITY) / 1000),
      Infinity,
    );
  }, [series, startTime]);

  const duration = useMemo(() => {
    if (startTime != null && endTime != null) return (endTime - startTime) / 1000;
    return series.reduce((max, serie) => {
      if (serie.data.length === 0) return max;
      return Math.max(max, (serie.data[serie.data.length - 1].x - serie.data[0].x) / 1000);
    }, -Infinity);
  }, [series, startTime, endTime]);

  // The interval is used in core-ui line chart to fill gaps with NaN Points
  // Without NaN points we would be showing a continuous line where data is missing
  // We assume here that the sampling rate is the same for each serie and that
  // the 2 first points of at least one serie are available, if not we cannot
  // compute the interval with a reasonable complexity
  const resolvedInterval = useMemo(() => {
    if (interval != null) return interval;

    const fromData = series.reduce((min, serie) => {
      if (serie.data.length < 2) return min;
      return Math.min(min, (serie.data[1].x - serie.data[0].x) / 1000);
    }, Infinity);
    if (Number.isFinite(fromData)) return fromData;

    // Nothing to measure a cadence from. The whole duration keeps a single-point serie
    // plottable — core-ui's gap filler bails out on a non-finite step — without inventing one.
    return Number.isFinite(duration) && duration > 0 ? duration : Infinity;
  }, [series, interval, duration]);

  return { coreUiSeries, colorSet, startingTimeStamp, interval: resolvedInterval, duration };
};
